/**
 * 媒體庫的清單檢視。
 *
 * 由 MediaLibrary 抽出來，讓它與 {@link MediaGrid} 只是同一組 props 的兩種畫法。
 * 欄位順序刻意把「檔案」放寬：ITRI 素材樹裡 `4_str4.txt` 出現在 6 個資料夾，
 * 檔名下方的路徑標籤才是真正用來分辨的資訊，不能被擠掉。
 */

import { Button } from './Button';
import { MediaMetaLine, type MediaViewProps } from './MediaItemView';
import { MediaThumb } from './MediaPreview';
import { formatBytes } from '../lib/media-upload';

export function MediaTable(props: MediaViewProps): JSX.Element {
  const {
    items,
    languages,
    selectedIds,
    onToggle,
    onToggleAll,
    allSelected,
    editing,
    onEditChange,
    onSave,
    saving,
    onPreview,
    onDelete,
    onShowUsage,
    onFolder,
  } = props;

  return (
    <div className="table-card">
      <table>
        <thead>
          <tr>
            <th style={{ width: 34 }}>
              <input
                type="checkbox"
                checked={allSelected}
                onChange={onToggleAll}
                title="全選本頁"
              />
            </th>
            <th style={{ width: 60 }}>預覽</th>
            <th>檔案</th>
            <th style={{ width: 70 }}>型別</th>
            <th style={{ width: 80 }}>大小</th>
            <th style={{ width: 90 }}>語系</th>
            <th style={{ width: 90 }}>sha256</th>
            <th style={{ width: 130 }}>建立時間</th>
            <th style={{ width: 120 }}>操作</th>
          </tr>
        </thead>
        <tbody>
          {items.length === 0 && (
            <tr>
              <td colSpan={9} className="empty-cell">
                此處尚無素材
              </td>
            </tr>
          )}
          {items.map((m) => {
            const isEditing = editing?.id === m.id;
            return (
              <tr key={m.id} style={selectedIds.has(m.id) ? { background: '#f5f8ff' } : {}}>
                <td>
                  <input
                    type="checkbox"
                    checked={selectedIds.has(m.id)}
                    onChange={() => onToggle(m.id)}
                  />
                </td>
                <td>
                  <button
                    onClick={() => onPreview(m)}
                    style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 0 }}
                    title="預覽"
                  >
                    <MediaThumb item={m} />
                  </button>
                </td>
                <td style={{ fontSize: 13, maxWidth: 320, wordBreak: 'break-all' }}>
                  {m.filename}
                  {isEditing && editing ? (
                    <input
                      type="text"
                      value={editing.tags}
                      onChange={(e) => onEditChange({ ...editing, tags: e.target.value })}
                      style={{ padding: 4, width: '100%', marginTop: 4 }}
                    />
                  ) : (
                    <MediaMetaLine item={m} onFolder={onFolder} onShowUsage={onShowUsage} />
                  )}
                </td>
                <td style={{ fontSize: 12 }}>{m.media_type}</td>
                <td style={{ fontSize: 12 }}>{formatBytes(Number(m.size_bytes))}</td>
                <td>
                  {isEditing && editing ? (
                    <select
                      value={editing.language}
                      onChange={(e) => onEditChange({ ...editing, language: e.target.value })}
                      style={{ padding: 4 }}
                    >
                      {languages.map((l) => (
                        <option key={l.value} value={l.value}>
                          {l.label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    (m.language ?? '—')
                  )}
                </td>
                <td style={{ fontFamily: 'monospace', fontSize: 11 }}>{m.sha256.slice(0, 8)}…</td>
                <td style={{ fontSize: 12 }}>{new Date(m.created_at).toLocaleString('zh-TW')}</td>
                {/*
                  nowrap：兩顆按鈕加間距恰好等於 120 px 欄寬的內容區，瀏覽器會把
                  按鈕壓到比文字還窄，「編輯」就被折成上下兩個字。表格是 auto
                  layout，宣告不折行後欄位會去跟 623 px 的檔名欄要空間。
                */}
                <td style={{ whiteSpace: 'nowrap' }}>
                  {isEditing && editing ? (
                    <div style={{ display: 'flex', gap: 4 }}>
                      <Button variant="primary" onClick={() => onSave(editing)} disabled={saving}>
                        儲存
                      </Button>
                      <Button variant="secondary" onClick={() => onEditChange(null)}>
                        取消
                      </Button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', gap: 4 }}>
                      <Button
                        variant="secondary"
                        onClick={() =>
                          onEditChange({
                            id: m.id,
                            language: m.language ?? '',
                            tags: m.tags.join(', '),
                          })
                        }
                      >
                        編輯
                      </Button>
                      <Button variant="danger" onClick={() => onDelete(m)}>
                        刪除
                      </Button>
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
